import type { AppState } from '@/types/appState';
import { JobSortingOptions } from '@shared/constants/jobSort';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useAppStore = defineStore('appState', () => {
  const loading = ref<AppState['loading']>(false);
  const sidebarOpen = ref<AppState['sidebarOpen']>(
    !localStorage.getItem('sidebarOpen')
      ? true
      : localStorage.getItem('sidebarOpen') === 'true'
  );

  //new job list filter &  sort
  const jobFilters = ref<AppState['jobFilters']>(
    JSON.parse(localStorage.getItem('jobFilters') as string) || {}
  );

  const jobSort = ref<AppState['jobSort']>(
    JSON.parse(localStorage.getItem('jobSort') as JobSortingOptions)
  );

  //----- old job list filters & sort -----

  const jobSorting = ref<AppState['jobSorting']>(
    JSON.parse(localStorage.getItem('jobSorting') as string) || {
      sortBy: 'created_at',
      sortDirection: 'asc'
    }
  );

  const jobsPerPage = ref<AppState['jobsPerPage']>(
    parseInt(localStorage.getItem('jobsPerPage') || '10')
  );
  const jobsContractorFilter = ref<AppState['jobsContractorFilter']>(
    localStorage.getItem('jobsContractorFilter') || null
  );
  const jobsProgramsFilter = ref<AppState['jobsProgramsFilter']>(
    JSON.parse(localStorage.getItem('jobsProgramsFilter') as string) || []
  );
  const jobsActionOwnersFilter = ref<AppState['jobsActionOwnersFilter']>(
    JSON.parse(localStorage.getItem('jobsActionOwnersFilter') as string) || []
  );
  const jobsPaymentStatusFilter = ref<AppState['jobsPaymentStatusFilter']>(
    JSON.parse(localStorage.getItem('jobsPaymentStatusFilter') as string) || []
  );
  const programsStatusFilter = ref<AppState['programsStatusFilter']>(
    JSON.parse(localStorage.getItem('programsStatusFilter') as string) || {}
  );
  const jobsOpsOwnersFilter = ref<AppState['jobsOpsOwnersFilter']>(
    JSON.parse(localStorage.getItem('jobsOpsOwnersFilter') as string) || []
  );
  const contractorsPerPage = ref<AppState['contractorsPerPage']>(
    parseInt(localStorage.getItem('contractorsPerPage') || '25')
  );
  const contractorFilters = ref<AppState['contractorFilters']>(
    JSON.parse(localStorage.getItem('contractorFilters') as string) || {}
  );

  const setJobSorting = (newJobSorting: AppState['jobSorting']) => {
    jobSorting.value = newJobSorting;
    localStorage.setItem('jobSorting', JSON.stringify(jobSorting.value));
  };
  const setJobsContractorFilter = (
    contractor: AppState['jobsContractorFilter']
  ) => {
    jobsContractorFilter.value = contractor;
    localStorage.setItem(
      'jobsContractorFilter',
      jobsContractorFilter.value || ''
    );
  };
  const setJobsProgramsFilter = (programs: AppState['jobsProgramsFilter']) => {
    jobsProgramsFilter.value = programs;
    localStorage.setItem(
      'jobsProgramsFilter',
      JSON.stringify(jobsProgramsFilter.value)
    );
  };
  const setJobsActionOwnersFilter = (
    actionOwners: AppState['jobsActionOwnersFilter']
  ) => {
    jobsActionOwnersFilter.value = actionOwners;
    localStorage.setItem(
      'jobsActionOwnersFilter',
      JSON.stringify(jobsActionOwnersFilter.value)
    );
  };
  const setJobsPaymentStatusFilter = (
    paymentStatus: AppState['jobsPaymentStatusFilter']
  ) => {
    jobsPaymentStatusFilter.value = paymentStatus;
    localStorage.setItem(
      'jobsPaymentStatusFilter',
      JSON.stringify(jobsPaymentStatusFilter.value)
    );
  };
  const setProgramsStatusFilter = (
    programStatus: AppState['programsStatusFilter']
  ) => {
    programsStatusFilter.value = programStatus;
    localStorage.setItem(
      'programsStatusFilter',
      JSON.stringify(programsStatusFilter.value)
    );
  };
  const setJobsOpsOwnersFilter = (
    opsOwners: AppState['jobsOpsOwnersFilter']
  ) => {
    jobsOpsOwnersFilter.value = opsOwners;
    localStorage.setItem(
      'jobsOpsOwnersFilter',
      JSON.stringify(jobsOpsOwnersFilter.value)
    );
  };
  const setContractorFilters = (
    newContractorFilters: AppState['contractorFilters']
  ) => {
    contractorFilters.value = newContractorFilters;
    localStorage.setItem(
      'contractorFilters',
      JSON.stringify(contractorFilters.value)
    );
  };
  const setContractorsPerPage = (
    newContractorsPerPage: AppState['contractorsPerPage']
  ) => {
    contractorsPerPage.value = newContractorsPerPage;
    localStorage.setItem(
      'contractorsPerPage',
      contractorsPerPage.value.toString()
    );
  };
  //----- end old job list filters & sort -----

  const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value;
    localStorage.setItem('sidebarOpen', sidebarOpen.value.toString());
  };

  //new job list combine sort & filter set
  const setJobSort = (newJobSort?: AppState['jobSort']) => {
    if (!newJobSort) {
      localStorage.removeItem('jobSort');
    } else {
      localStorage.setItem('jobSort', JSON.stringify(newJobSort));
    }
    jobSort.value = newJobSort;
  };
  const setJobFilters = (filters: AppState['jobFilters']) => {
    jobFilters.value = filters;
    localStorage.setItem('jobFilters', JSON.stringify(jobFilters.value));
  };

  return {
    loading,
    sidebarOpen,
    toggleSidebar,
    jobFilters,
    jobSorting,
    setJobSorting,
    jobsPerPage,
    setJobsContractorFilter,
    jobsContractorFilter,
    setJobsProgramsFilter,
    jobsProgramsFilter,
    setJobsActionOwnersFilter,
    jobsActionOwnersFilter,
    setJobsOpsOwnersFilter,
    jobsOpsOwnersFilter,
    contractorsPerPage,
    setContractorsPerPage,
    contractorFilters,
    setContractorFilters,
    jobsPaymentStatusFilter,
    setJobsPaymentStatusFilter,
    programsStatusFilter,
    setProgramsStatusFilter,
    jobSort,
    setJobSort,
    setJobFilters
  };
});
